import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaClock,
  FaStar,
  FaUser,
  FaHandshake,
  FaChevronLeft,
  FaIdCard,
  FaImages,
  FaTimes,
  FaShieldAlt,
  FaCalendarCheck,
  FaClipboardList,
  FaExternalLinkAlt,
  FaChartLine,
  FaMap,
  FaChevronRight,
} from "react-icons/fa";
import { QrCode } from "lucide-react";

import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import BackMethods from "../../back_component/BackMethods";
import Establishment from "../../models/Establishment";
import img from "../../1.webp";
import ComplaintForm from "../PublicUsers/ComplaintForm";

import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

import "./EstablishmentDetailsPage.css";

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function EstDetailKV({ label, value, icon, valueSingleLine, compactRow }) {
  const display =
    value === null || value === undefined || value === ""
      ? "—"
      : String(value);
  if (compactRow) {
    return (
      <div className="est-kv est-kv--compact-row">
        {icon ? <span className="est-kv__ico">{icon}</span> : null}
        <span className="est-kv__l">{label}</span>
        <span
          className={`est-kv__v${valueSingleLine ? " est-kv__v--nowrap" : ""}`}
          title={display}
        >
          {display}
        </span>
      </div>
    );
  }
  return (
    <div className="est-kv">
      <div className="est-kv__row">
        {icon ? <span className="est-kv__ico">{icon}</span> : null}
        <span className="est-kv__l">{label}</span>
      </div>
      <div
        className={`est-kv__v${valueSingleLine ? " est-kv__v--nowrap" : ""}`}
        title={display}
      >
        {display}
      </div>
    </div>
  );
}

function EstPropertyStripCell({ label, value }) {
  const display =
    value === null || value === undefined || value === ""
      ? "—"
      : String(value);
  return (
    <div className="est-property-strip__cell">
      <div className="est-property-strip__label">{label}</div>
      <div className="est-property-strip__value" title={display}>
        {display}
      </div>
    </div>
  );
}

/** يقسّم المصفوفة إلى أزواج [a,b], [c,d], … لعرض شخصين في نفس الجزء */
function chunkPairs(arr) {
  const out = [];
  for (let i = 0; i < arr.length; i += 2) out.push(arr.slice(i, i + 2));
  return out;
}

export default function EstablishmentDetailsPage({ isPublicView = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { backEnd_request } = BackMethods();

  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const endpoint = isPublicView
          ? `/public/establishments/${id}`
          : `/Establishments/${id}`;
        const { data } = await backEnd_request.get(endpoint);
        setEst(new Establishment(data));
      } catch (err) {
        setError(err.response?.data || "خطأ في الاتصال بالخادم");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isPublicView]);

  if (loading) {
    return (
      <div className="est-details-loading bg-light" dir="rtl">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">جاري التحميل…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="est-details-error" dir="rtl">
        <div className="alert alert-danger shadow-sm text-center mb-0 w-100">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      </div>
    );
  }

  if (!est) return null;

  const allPhotos =
    !isPublicView && est?.photos?.length > 0
      ? est.photos.map((p) => p.photoUrl)
      : [est.externalPhotoUrl || est?.externalPhoto?.photoUrl || img];

  const nextImg = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % allPhotos.length);
  };
  const prevImg = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length);
  };

  const personsList = Array.isArray(est.persons) ? est.persons : [];
  const activitiesList = Array.isArray(est.activities) ? est.activities : [];

  const mapBlock = (
    <section className="est-panel est-map-panel">
      <div className="est-panel__head est-panel__head-row">
        <span>
          <FaMapMarkerAlt className="ms-1" /> الموقع
        </span>
        {est.lat && est.lng ? (
          <a
            href={`https://www.google.com/maps?q=${est.lat},${est.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="est-link-maps"
          >
            <FaExternalLinkAlt size={14} /> خرائط جوجل
          </a>
        ) : null}
      </div>
      <div className="est-panel__body">
        <div className="est-mini-map">
          {est.lat && est.lng ? (
            <MapContainer
              center={[est.lat, est.lng]}
              zoom={15}
              style={{ height: "100%", width: "100%" }}
              zoomControl={false}
              scrollWheelZoom={false}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={[est.lat, est.lng]} />
            </MapContainer>
          ) : (
            <div className="est-map-placeholder">
              <FaMap size={40} className="opacity-30" />
              <span>الموقع غير محدد على الخريطة</span>
            </div>
          )}
        </div>
        <div className="est-address-box">
          <div className="est-address-box__label">العنوان التفصيلي</div>
          <p className="est-address-box__text">{est.city +" - " +est.district+ ' : '}{est.addressText || "—"}</p>
        </div>
        {/* <div className="est-address-meta">
          <div className="est-address-meta__row">
            <span className="text-muted">المدينة</span>
            <span className="fw-bold">{est.city || "—"}</span>
          </div>
          <div className="est-address-meta__row">
            <span className="text-muted">المنطقة</span>
            <span className="fw-bold">{est.district || "—"}</span>
          </div>
          <div className="est-address-meta__row">
            <span className="text-muted">المحافظة</span>
            <span className="fw-bold">{est.governorate || "—"}</span>
          </div>
        </div> */}
        {est.phone ? (
          <a href={`tel:${est.phone}`} className="est-call-btn">
            <span className="d-flex align-items-center">
              <span className="est-call-btn__ico">
                <FaPhoneAlt size={16} />
              </span>
              {est.phone}
            </span>
            <FaChevronLeft size={14} />
          </a>
        ) : null}
      </div>
    </section>
  );

  const hoursCompact =
    est.openingTime && est.closingTime
      ? `${String(est.openingTime).trim()}–${String(est.closingTime).trim()}`
      : "—";

  const factsPanel = (
    <section className="est-compact-surface" aria-label="حقول المنشأة">
      <div
        className={`est-kv-grid est-kv-grid--inline-row${isPublicView ? " est-kv-grid--cols-3" : " est-kv-grid--facts-admin"}`}
      >
        <EstDetailKV
          label="نوع البيع"
          value={est.salesType}
          icon={<FaHandshake size={16} />}
        />
        <EstDetailKV
          label="السجل التجاري"
          value={est.tradeRegistryNumber}
          icon={<FaIdCard size={16} />}
        />
        <EstDetailKV
          label="التقييم"
          value={
            isPublicView
              ? `${est.rating?.avg ?? 0} / 5`
              : `${est.ratingOutOfTen ?? "—"} / 10`
          }
          icon={<FaStar className="text-warning" size={isPublicView ? 16 : 14} />}
          ratingCompact={!isPublicView}
        />
        {!isPublicView ? (
          <EstDetailKV
            label="ساعات العمل"
            value={hoursCompact}
            icon={<FaClock size={16} />}
            valueSingleLine
            compactRow
          />
        ) : null}
      </div>
    </section>
  );

  const propertyBlock = !isPublicView ? (
    <section className="est-compact-surface" aria-label="حقول العقار">
      <div className="est-property-inline-grid">
        <EstPropertyStripCell label="المالك" value={est.propertyOwnerName} />
        <EstPropertyStripCell label="المساحة" value={est.propertyArea} />
        <EstPropertyStripCell label="نوع الملكية" value={est.propertyType} />
        <EstPropertyStripCell
          label="رأس المال التقديري"
          value={est.estimatedCapital}
        />
      </div>
    </section>
  ) : null;

  const personsPanel = !isPublicView ? (
    <section className="est-panel" aria-labelledby="est-section-persons">
      <div id="est-section-persons" className="est-panel__head">
        <FaUser /> الأشخاص المرتبطون ({personsList.length})
      </div>
      <div className="est-panel__body">
        {personsList.length === 0 ? (
          <p className="est-empty-hint">لا يوجد أشخاص مسجلون لهذه المنشأة</p>
        ) : (
          <div className="est-persons-pairs-grid">
            {chunkPairs(personsList).map((pair, idx) => (
              <div
                key={idx}
                className={`est-person-pair${pair.length === 1 ? " est-person-pair--single" : ""}`}
              >
                {pair.map((p, i) => (
                  <div key={i} className="est-person-pair__slot">
                    <FaUser size={15} aria-hidden className="est-person-pair__ico flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="est-person-row__name text-truncate">
                        {p.firstName} {p.lastName}
                      </div>
                      <div className="est-person-row__phone">
                        {p.phone || "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  ) : null;

  const activitiesPanel = !isPublicView ? (
    <section className="est-panel" aria-labelledby="est-section-activities">
      <div id="est-section-activities" className="est-panel__head">
        <FaClipboardList /> الأنشطة التجارية ({activitiesList.length})
      </div>
      <div className="est-panel__body">
        {activitiesList.length === 0 ? (
          <p className="est-empty-hint">لا توجد أنشطة مسجلة</p>
        ) : (
          <div className="est-activities-wrap">
            {activitiesList.map((a, i) => (
              <span key={i} className="est-activity-tag">
                {a.activityName}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  ) : null;

  return (
    <div className="est-details-page" dir="rtl">
      <header className="est-details-hero">
        <div className="est-details-hero__inner">
          <div className="est-details-hero__top">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="est-details-back"
              style={{ direction: "ltr" }}
            >
              <FaChevronLeft className="me-1" />
              العودة
            </button>
          </div>
          <div className="est-details-hero__row">
            <div className="est-details-hero__lead">
              <div
                className="est-profile-thumb-wrap"
                role="button"
                tabIndex={0}
                onClick={() => setShowGallery(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowGallery(true);
                  }
                }}
              >
                <div className="est-profile-thumb">
                  <img src={allPhotos[0]} alt={est.name} />
                </div>
                {allPhotos.length > 1 ? (
                  <div className="est-profile-thumb__count">
                    <FaImages className="ms-1" /> {allPhotos.length}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="est-details-hero__title-block">
              <div className="est-details-hero__title-top">
                <span className="est-badge-type">
                  {est.facilityType || "منشأة تجارية"}
                </span>
                <h1 className="est-details-hero__name">{est.name}</h1>
              </div>
              <div className="est-details-hero__title-footer">
                <p className="est-details-hero__loc">
                  <FaMapMarkerAlt className="text-warning flex-shrink-0" />
                  <span>
                    {est.governorate} — {est.city} — {est.district}
                  </span>
                </p>
                {!isPublicView ? (
                  <div
                    className="est-details-hero__toolbar-inline"
                    aria-label="إجراءات المنشأة"
                  >
                    <div className="est-toolbar">
                      <button
                        type="button"
                        className="est-toolbar__btn"
                        onClick={() =>
                          navigate(`/admin/visits?establishment=${id}`)
                        }
                      >
                        <FaCalendarCheck /> الزيارات
                      </button>
                      <button
                        type="button"
                        className="est-toolbar__btn"
                        onClick={() =>
                          navigate(`/admin/violations?establishment=${id}`)
                        }
                      >
                        <FaIdCard /> الضبوط
                      </button>
                      <button
                        type="button"
                        className="est-toolbar__btn"
                        onClick={() =>
                          navigate(`/admin/complaints?establishment=${id}`)
                        }
                      >
                        <FaClipboardList /> الشكاوى
                      </button>
                      <button
                        type="button"
                        className="est-toolbar__btn"
                        onClick={() =>
                          navigate(
                            `/admin/price-survey-report?establishmentUid=${est.fieldUID}`
                          )
                        }
                      >
                        <FaChartLine /> سبر الأسعار
                      </button>
                      <button
                        type="button"
                        className="est-toolbar__btn"
                        onClick={() =>
                          navigate("/admin/generate-uid", {
                            state: {
                              uid: est.fieldUID,
                              id: est.establishmentId,
                            },
                          })
                        }
                      >
                        <QrCode size={16} strokeWidth={2} /> QR
                      </button>
                      <button
                        type="button"
                        className="est-toolbar__btn"
                        onClick={() =>
                          navigate("/admin/inspection-mission/create", {
                            state: {
                              fieldUID: est.fieldUID,
                              establishmentName: est.name,
                            },
                          })
                        }
                      >
                        <FaShieldAlt /> إسناد مهمة
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="est-details-shell">
        {isPublicView ? (
          <div className="est-layout est-layout--public">
            <div className="est-public-row-top">
              {factsPanel}
              {mapBlock}
            </div>
            <section className="est-panel est-complaint-wrap">
              <div className="est-panel__head">تقديم بلاغ</div>
              <div className="est-panel__body">
                <ComplaintForm
                  establishmentId={id}
                  backEnd_request={backEnd_request}
                />
              </div>
            </section>
          </div>
        ) : (
          <div className="est-layout est-layout--admin">
            {/*
              RTL: أول عمود في DOM = يمين (الشاشة)، ثاني عمود = يسار (الخريطة).
            */}
            <div className="est-admin-main">
              <div className="est-admin-right-screen">
                <div className="est-admin-right-row est-admin-right-row--property">
                  {propertyBlock}
                </div>
                <div className="est-admin-right-row est-admin-right-row--facts">
                  {factsPanel}
                </div>
                <div className="est-admin-right-row est-admin-right-row--split est-admin-split">
                  {personsPanel}
                  {activitiesPanel}
                </div>
              </div>
              <div className="est-admin-map-col">{mapBlock}</div>
            </div>
          </div>
        )}
      </div>

      {showGallery ? (
        <div
          className="est-gallery-portal"
          role="presentation"
          onClick={() => setShowGallery(false)}
        >
          <button
            type="button"
            className="est-gallery-portal__close"
            aria-label="إغلاق"
            onClick={() => setShowGallery(false)}
          >
            <FaTimes />
          </button>
          <div
            className="est-gallery-portal__main"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={allPhotos[currentIndex]} alt="" />
            <div className="est-gallery-portal__nav">
              <button type="button" onClick={prevImg}>
                <FaChevronRight />
              </button>
              <span>
                {currentIndex + 1} / {allPhotos.length}
              </span>
              <button type="button" onClick={nextImg}>
                <FaChevronLeft />
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
